<template>
  <div
    v-if="visible"
    ref="el"
    class="net-question"
    :class="{ 'is-selected': isSelected, 'is-highlighted': isHighlighted }"
    :style="{
      width: `${size.width}px`,
      height: `${size.height}px`,
      left: `${size.x}px`,
      top: `${size.y}px`
    }"
    @contextmenu.prevent="onContextMenu"
    @pointerdown.exact="onMouseDown"
    @pointerover="onMouseIn"
    @pointerout="onMouseOut"
  >
    <net-input-text
      v-if="editable"
      ref="inputElement"
      class="net-question__text" 
      :value="text"
      :stylable="true"
      :line-height="1.5"
      :position="{ x: size.x, y: size.y }"
      @update:value="updateText($event)"
    ></net-input-text>
    <formatted-text v-else :value="text" class="net-question__text" />
  </div>
</template>

<script setup lang="ts">
import { computed, reactive, ref, watch } from "vue";

import NetInputText from "@editor/net/NetInputText.vue";
import FormattedText from "@core/ui/FormattedText.vue";
import { useQuestionStore } from "@editor/items/question/question.store";
import { useSelectionStore } from "@editor/store/editor/selection.store";
import { useNetDrag } from "@editor/net/useNetDrag";
import type { QuestionItem } from "./QuestionItem";

const props = defineProps<{
  editable: boolean
}>();


const emit = defineEmits<{
  (event: "select", question: QuestionItem): void;
  (event: "contextmenu", mouseEvent: MouseEvent, question: QuestionItem): void;
}>();

const question = useQuestionStore();

const el = ref(null); // ref to main element

const hover = ref(false);

const text = computed(() => {
  return question.text;
});

function updateText(newText: string) {
  if (!props.editable) return;
  question.set({ text: newText });
}

const visible = computed(() => {
  return question.hidden == false && text.value != "";
});

const size = reactive({
  x: 0,
  y: 0,
  width: 0,
  height: 0
});

useNetDrag(el, {
  draggable: true,
  resizable: true,
  minWidth: 100,
  minHeight: 40,
  size: size,
  visible: visible,

  onDragMove(event, coords, delta) {
    size.x = size.x + delta.x;
    size.y = size.y + delta.y;
  },
  onDragEnd() {
    question.set({
      x: size.x,
      y: size.y
    });
  },
  onResize({ x, y, width, height }) {
    size.x = x;
    size.y = y;
    size.width = width;
    size.height = height;
  },
  onResizeEnd() {
    console.info("questionheight", size.height);

    question.set({
      x: size.x,
      y: size.y,
      height: size.height,
      width: size.width
    });
  }
});
  
const isSelected = computed(() => {
  const selectedItem = useSelectionStore().selectedItem;
  if (selectedItem == null) return false;
  return selectedItem.type === "question";
});
const isHighlighted = computed(() => {
  const highlightedQuestion = useSelectionStore().highlighted.question || [];
  return highlightedQuestion.length > 0;
});
    
watch(() => question.x, (value) => {
  size.x = value;
}, {
  immediate: true
});
watch(() => question.y, (value) => {
  size.y = value;
}, {
  immediate: true
});
watch(() => question.width, (value) => {
  size.width = value;
}, {
  immediate: true
});
watch(() => question.height, (value) => {
  size.height = value;
}, {
  immediate: true
});

function onMouseIn() {
  hover.value = true;
}

function onMouseOut() {
  hover.value = false;
}

function onContextMenu(event: MouseEvent) {
  emit("contextmenu", event, question);
}

function onMouseDown(mouseEvent: MouseEvent & { hasBeenHandled?: boolean }) {
  // don't handle middle mouse key.
  if (mouseEvent.which === 2) {
    return;
  }
  mouseEvent.hasBeenHandled = true;

  emit("select", question);
}
</script>

<style lang="scss">
.net-question {
  touch-action: none;
  pointer-events: auto;
  position: absolute;
  background-color: var(--color-background-deep);
  outline: 1px solid transparent;
  box-shadow: 0 3px 6px rgba(0, 0, 0, 0.15);
  padding: 0.5em;
  overflow: hidden;

  &.is-selected {
    outline-color: var(--color-action);
  }
  &.is-highlighted {
    outline-color: var(--color-action);
    outline-width: 1px;
  }

  &__text {
    font-weight: 400;
    font-size: 1em;
    line-height: 24px;

    height: 100%;
    max-height: 100%;
    overflow: hidden;
    grid-template-rows: 1fr !important;
  }
}
</style>
